import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Zap } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useMasters } from "../form/engine";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const SUPPLIER_ROLES = ["farmer", "vendor", "admin"];
const UNITS = ["kg", "g", "L", "ml", "pc", "dozen", "box"];
const NEXT = { placed: "confirmed", confirmed: "dispatched", dispatched: "delivered" };

// SupplyNet — B2B ingredient marketplace: suppliers list live stock, kitchens
// compare suppliers (nearest + cheapest), bulk-order with discounts, emergency sourcing.
export const SupplyNetPanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const masters = useMasters();
  const isSupplier = SUPPLIER_ROLES.includes(user.role);
  const [tab, setTab] = useState("market");
  const [market, setMarket] = useState(null);
  const [q, setQ] = useState("");
  const [emergency, setEmergency] = useState(false);
  const [cart, setCart] = useState({});
  const [mine, setMine] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [lForm, setLForm] = useState({ ingredient: "", unit: "kg", price_per_unit: "", qty_available: "", location: "", emergency: false, bulk_min_qty: "", bulk_discount_pct: "" });
  const [busy, setBusy] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [sub, setSub] = useState(null);
  const [subs, setSubs] = useState([]);
  const isAdmin = user.role === "admin";
  const isKitchen = !isSupplier || isAdmin;

  const load = () => {
    axios.get(`${API}/supply/market?q=${encodeURIComponent(q)}&emergency=${emergency}`, headers()).then((r) => setMarket(r.data.market)).catch(() => setMarket([]));
    if (isSupplier) {
      axios.get(`${API}/supply/listings/mine`, headers()).then((r) => setMine(r.data.listings)).catch(() => {});
      axios.get(`${API}/supply/orders/incoming`, headers()).then((r) => setIncoming(r.data.orders)).catch(() => {});
      axios.get(`${API}/supply/subscription/mine`, headers()).then((r) => setSub(r.data.subscription)).catch(() => {});
    }
    if (isKitchen) axios.get(`${API}/supply/forecast`, headers()).then((r) => setForecast(r.data)).catch(() => setForecast({ items: [], insight: "" }));
    if (isAdmin) axios.get(`${API}/supply/subscriptions`, headers()).then((r) => setSubs(r.data.subscriptions)).catch(() => {});
    axios.get(`${API}/supply/orders/mine`, headers()).then((r) => setMyOrders(r.data.orders)).catch(() => {});
  };
  useEffect(load, [q, emergency]);

  const subscribe = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/supply/subscribe`, {}, headers());
      toast.success("Premium request sent — our team activates it after payment (₹999/mo) ⭐");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const activateSub = async (id) => {
    try {
      await axios.post(`${API}/supply/subscriptions/${id}/activate`, {}, headers());
      toast.success("Subscription activated ⭐");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const cartList = Object.values(cart);
  const cartTotal = cartList.reduce((a, c) => {
    const gross = c.price_per_unit * c.qty;
    const disc = c.bulk_min_qty && c.qty >= c.bulk_min_qty ? Math.round((gross * c.bulk_discount_pct) / 100) : 0;
    return a + gross - disc;
  }, 0);

  const placeOrder = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/supply/orders`, { items: cartList.map((c) => ({ supply_id: c.id, qty: c.qty })), note: "" }, headers());
      toast.success(`Ordered from ${data.suppliers} supplier${data.suppliers > 1 ? "s" : ""} 🥕 ₹${cartTotal}`);
      setCart({});
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addListing = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/supply/listings`, {
        ...lForm,
        price_per_unit: Number(lForm.price_per_unit),
        qty_available: Number(lForm.qty_available),
        bulk_min_qty: Number(lForm.bulk_min_qty) || 0,
        bulk_discount_pct: Number(lForm.bulk_discount_pct) || 0,
      }, headers());
      toast.success("Listing live on SupplyNet 🥕");
      setLForm({ ingredient: "", unit: "kg", price_per_unit: "", qty_available: "", location: "", emergency: false, bulk_min_qty: "", bulk_discount_pct: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const advance = async (id, status) => {
    try {
      await axios.post(`${API}/supply/orders/${id}/status`, { status }, headers());
      toast.success(`Order ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const tabs = [
    { id: "market", label: `🥕 ${t(lang, "sn_market")}` },
    ...(isKitchen ? [{ id: "forecast", label: `📈 ${t(lang, "fc_title")}` }] : []),
    ...(isSupplier ? [{ id: "mine", label: `📋 ${t(lang, "sn_mine")}` }, { id: "incoming", label: `📥 ${t(lang, "sn_incoming")}` }] : [{ id: "orders", label: `📦 ${t(lang, "sn_orders")}` }]),
    ...(isAdmin ? [{ id: "subs", label: `⭐ ${t(lang, "pm_subs")}` }] : []),
  ];

  const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

  return (
    <div className="space-y-5" data-testid="panel-supplynet">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`sn-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#22C55E] to-[#06B6D4] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "market" && (
        <>
          <div className="flex gap-2">
            <input data-testid="sn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tomato, paneer, rice…" className={inputCls} />
            <button data-testid="sn-emergency" onClick={() => setEmergency(!emergency)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                emergency ? "bg-[#F97316] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
              <Zap className="h-3.5 w-3.5" /> {t(lang, "sn_emergency")}
            </button>
          </div>
          {market === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : market.length === 0 ? (
            <p className="text-sm text-slate-500">No suppliers match — {isSupplier ? "list your stock in My listings." : "try another ingredient."}</p>
          ) : (
            market.map((g) => (
              <div key={g.ingredient} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid={`sn-group-${g.ingredient.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold">{g.ingredient}</p>
                  <p className="font-jbmono text-[10px] text-slate-500">
                    {g.offers.length} {t(lang, "sn_offers")} · {t(lang, "sn_best")} <span className="font-bold text-[#22C55E]">₹{g.best_price}</span>
                  </p>
                </div>
                <div className="mt-3 space-y-2">
                  {g.offers.map((o) => (
                    <div key={o.id} data-testid={`sn-offer-${o.id.slice(-6)}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-[#09090B]/60 p-3">
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-xs font-bold">
                          {o.supplier_name}
                          {o.boosted && <span className="rounded-full bg-[#F97316]/15 px-2 py-0.5 font-jbmono text-[8px] font-bold text-[#F97316]" data-testid={`sn-boosted-${o.id.slice(-6)}`}>⭐ {t(lang, "pm_badge")}</span>}
                          {o.emergency && <span className="rounded-full bg-[#F97316]/10 px-2 py-0.5 font-jbmono text-[8px] font-bold text-[#F97316]">⚡ {t(lang, "sn_emergency")}</span>}
                          {o.nearness > 0 && <span className="rounded-full bg-[#06B6D4]/10 px-2 py-0.5 font-jbmono text-[8px] font-bold text-[#06B6D4]">📍 {t(lang, "sn_near")}</span>}
                        </p>
                        <p className="font-jbmono text-[10px] text-slate-500">
                          ₹{o.price_per_unit}/{o.unit} · {o.qty_available}{o.unit} · {o.location}
                          {o.bulk_min_qty > 0 && <span className="text-[#22C55E]"> · {t(lang, "sn_bulk")} {o.bulk_min_qty}+ → −{o.bulk_discount_pct}%</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input
                          data-testid={`sn-qty-${o.id.slice(-6)}`}
                          type="number" min="1" max={o.qty_available}
                          value={cart[o.id]?.qty || ""}
                          onChange={(e) => {
                            const qty = Math.min(o.qty_available, Math.max(0, Number(e.target.value) || 0));
                            setCart((c) => (qty ? { ...c, [o.id]: { ...o, qty } } : Object.fromEntries(Object.entries(c).filter(([k]) => k !== o.id))));
                          }}
                          placeholder={o.unit}
                          className="w-24 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-center font-jbmono text-sm text-white outline-none focus:border-[#22C55E]/60"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
          {cartList.length > 0 && (
            <div className="sticky bottom-4 flex items-center justify-between gap-3 rounded-2xl border border-[#22C55E]/40 bg-[#09090B]/95 p-4 shadow-2xl backdrop-blur-xl" data-testid="sn-cart">
              <p className="font-jbmono text-xs text-slate-300">
                {cartList.length} {t(lang, "sn_items")} · <span className="font-bold text-[#22C55E]">₹{cartTotal}</span>
              </p>
              <button data-testid="sn-order-submit" onClick={placeOrder} disabled={busy}
                className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
                {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : t(lang, "sn_order")}
              </button>
            </div>
          )}
        </>
      )}

      {tab === "forecast" && isKitchen && (
        <div className="space-y-3" data-testid="sn-forecast">
          {forecast === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : (
            <>
              {forecast.insight && (
                <div className="rounded-2xl border border-[#06B6D4]/30 bg-[#06B6D4]/[0.06] p-4" data-testid="sn-insight">
                  <p className="font-jbmono text-[9px] uppercase tracking-widest text-[#06B6D4]">🤖 FUNZI</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-200">{forecast.insight}</p>
                </div>
              )}
              {forecast.items.length === 0 ? (
                <p className="text-sm text-slate-500">{t(lang, "fc_empty")}</p>
              ) : (
                forecast.items.map((f) => (
                  <div key={f.ingredient} data-testid={`sn-fc-${f.ingredient.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold">
                        {f.ingredient}
                        <span className={`ml-2 rounded-full px-2 py-0.5 font-jbmono text-[8px] font-bold uppercase tracking-wider ${
                          f.status === "reorder" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-[#22C55E]/10 text-[#22C55E]"}`}>
                          {t(lang, f.status === "reorder" ? "fc_reorder" : "fc_stocked")}
                        </span>
                      </p>
                      <p className="font-jbmono text-[10px] text-slate-500">
                        {f.weekly_usage}{f.unit}{t(lang, "fc_weekly")} · → {t(lang, "fc_order")} {f.suggested_qty}{f.unit}
                        {f.best_price ? ` · ${t(lang, "sn_best")} ₹${f.best_price}/${f.unit} (${f.best_supplier})` : " · no live offers"}
                      </p>
                    </div>
                    {f.best_supply_id && (
                      <button data-testid={`sn-fc-order-${f.ingredient.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={async () => {
                          const qty = Math.max(1, Math.min(f.suggested_qty, f.best_qty || f.suggested_qty));
                          try {
                            await axios.post(`${API}/supply/orders`, { items: [{ supply_id: f.best_supply_id, qty }], note: "FUNZI forecast reorder" }, headers());
                            toast.success(`${f.ingredient} reordered — ${qty}${f.unit} 🥕`);
                            load();
                          } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail));
                          }
                        }}
                        className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-4 py-2 font-display text-[10px] font-bold uppercase tracking-wider text-[#09090B]">
                        {t(lang, "fc_order")} {Math.max(1, Math.min(f.suggested_qty, f.best_qty || f.suggested_qty))}{f.unit}
                        {f.best_qty > 0 && f.best_qty < f.suggested_qty ? " ⚠️" : ""}
                      </button>
                    )}
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {tab === "subs" && isAdmin && (
        <div className="space-y-2.5" data-testid="sn-subs">
          {subs.length === 0 && <p className="text-sm text-slate-500">No subscription requests yet.</p>}
          {subs.map((s) => (
            <div key={s.id} data-testid={`sn-sub-${s.id.slice(-6)}`} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{s.user_name}</p>
                <p className="font-jbmono text-[10px] text-slate-500">₹{s.price}/mo · {s.status}{s.expires_at ? ` · till ${String(s.expires_at).slice(0, 10)}` : ""}</p>
              </div>
              {s.status === "pending_payment" && (
                <button data-testid={`sn-activate-${s.id.slice(-6)}`} onClick={() => activateSub(s.id)}
                  className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-4 py-2 text-[10px] font-bold text-[#09090B]">
                  {t(lang, "pm_activate")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "mine" && isSupplier && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F97316]/30 bg-[#F97316]/[0.06] p-4" data-testid="sn-premium">
            <div>
              <p className="font-display text-sm font-bold">⭐ SupplyNet {t(lang, "pm_badge")}</p>
              <p className="mt-0.5 font-jbmono text-[10px] text-slate-400">{t(lang, "pm_hint")}</p>
            </div>
            {sub?.status === "active" ? (
              <span className="rounded-full bg-[#22C55E]/15 px-4 py-2 font-jbmono text-[10px] font-bold text-[#22C55E]" data-testid="sn-premium-active">
                {t(lang, "pm_active")} {String(sub.expires_at).slice(0, 10)}
              </span>
            ) : sub?.status === "pending_payment" ? (
              <span className="rounded-full bg-[#F97316]/15 px-4 py-2 font-jbmono text-[10px] font-bold text-[#F97316]" data-testid="sn-premium-pending">
                {t(lang, "pm_pending")}
              </span>
            ) : (
              <button data-testid="sn-subscribe" onClick={subscribe} disabled={busy}
                className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
                {t(lang, "pm_go")} · ₹999
              </button>
            )}
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="sn-create">
            <p className="font-display text-sm font-bold">{t(lang, "sn_add")}</p>
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div>
                <input data-testid="sn-new-ingredient" value={lForm.ingredient} onChange={(e) => setLForm({ ...lForm, ingredient: e.target.value })}
                  placeholder="Ingredient" list="sn-ingredients" className={inputCls} />
                <datalist id="sn-ingredients">
                  {(masters.ingredients || []).map((i) => <option key={i} value={i} />)}
                </datalist>
              </div>
              <input data-testid="sn-new-location" value={lForm.location} onChange={(e) => setLForm({ ...lForm, location: e.target.value })} placeholder="Location (market, area, city)" className={inputCls} />
              <div className="flex gap-2">
                <input data-testid="sn-new-price" type="number" value={lForm.price_per_unit} onChange={(e) => setLForm({ ...lForm, price_per_unit: e.target.value })} placeholder="₹ / unit" className={inputCls} />
                <input data-testid="sn-new-qty" type="number" value={lForm.qty_available} onChange={(e) => setLForm({ ...lForm, qty_available: e.target.value })} placeholder="Stock qty" className={inputCls} />
              </div>
              <div className="flex gap-2">
                <input data-testid="sn-new-bulkmin" type="number" value={lForm.bulk_min_qty} onChange={(e) => setLForm({ ...lForm, bulk_min_qty: e.target.value })} placeholder="Bulk min qty" className={inputCls} />
                <input data-testid="sn-new-bulkpct" type="number" value={lForm.bulk_discount_pct} onChange={(e) => setLForm({ ...lForm, bulk_discount_pct: e.target.value })} placeholder="Bulk −%" className={inputCls} />
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {UNITS.map((u) => (
                <button key={u} data-testid={`sn-unit-${u}`} onClick={() => setLForm({ ...lForm, unit: u })}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${lForm.unit === u ? "bg-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                  {u}
                </button>
              ))}
              <button data-testid="sn-new-emergency" onClick={() => setLForm({ ...lForm, emergency: !lForm.emergency })}
                className={`ml-auto flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-bold ${lForm.emergency ? "bg-[#F97316] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                <Zap className="h-3 w-3" /> {t(lang, "sn_emergency")}
              </button>
            </div>
            <button data-testid="sn-create-submit" onClick={addListing} disabled={busy || !lForm.ingredient || !lForm.price_per_unit || !lForm.qty_available || !lForm.location}
              className="mt-3 rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : t(lang, "sn_add")}
            </button>
          </div>
          {mine.map((l) => (
            <div key={l.id} data-testid={`sn-my-${l.id.slice(-6)}`} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{l.ingredient} <span className={l.active ? "text-[#22C55E]" : "text-slate-500"}>· {l.active ? "live" : "paused"}</span></p>
                <p className="font-jbmono text-[10px] text-slate-500">₹{l.price_per_unit}/{l.unit} · {l.qty_available}{l.unit} left · {l.location}</p>
              </div>
              <button data-testid={`sn-toggle-${l.id.slice(-6)}`}
                onClick={async () => { await axios.patch(`${API}/supply/listings/${l.id}`, { active: !l.active }, headers()); load(); }}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:text-white">
                {l.active ? "Pause" : "Activate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {(tab === "incoming" || tab === "orders") && (
        <div className="space-y-2.5">
          {(tab === "incoming" ? incoming : myOrders).length === 0 && <p className="text-sm text-slate-500">Nothing here yet.</p>}
          {(tab === "incoming" ? incoming : myOrders).map((o) => (
            <div key={o.id} data-testid={`sn-order-${o.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-bold">{o.items.map((i) => `${i.qty}${i.unit} ${i.ingredient}`).join(", ")}</p>
                  <p className="font-jbmono text-[10px] text-slate-500">
                    {tab === "incoming" ? `${t(lang, "sn_from")}: ${o.buyer_name}` : `${t(lang, "sn_from")}: ${o.supplier_name}`} · {String(o.created_at).slice(0, 10)}
                    {o.saved > 0 && <span className="text-[#22C55E]"> · −₹{o.saved} {t(lang, "sn_saved")}</span>}
                  </p>
                </div>
                <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">₹{o.total}</span>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                  o.status === "delivered" ? "bg-[#22C55E]/10 text-[#22C55E]" : o.status === "placed" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400"}`}>
                  {o.status}
                </span>
                {tab === "incoming" && NEXT[o.status] && (
                  <button data-testid={`sn-advance-${o.id.slice(-6)}`} onClick={() => advance(o.id, NEXT[o.status])}
                    className="rounded-full bg-[#06B6D4] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">
                    → {NEXT[o.status]}
                  </button>
                )}
                {o.status === "placed" && (
                  <button data-testid={`sn-cancel-${o.id.slice(-6)}`} onClick={() => advance(o.id, "cancelled")}
                    className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">{t(lang, "cancel")}</button>
                )}
                <button data-testid={`sn-chat-${o.id.slice(-6)}`}
                  onClick={() => window.dispatchEvent(new CustomEvent("funz:open-chat", { detail: { kind: "supply", ref_id: o.id } }))}
                  className="ml-auto rounded-full border border-[#06B6D4]/40 px-3 py-1.5 font-jbmono text-[9px] font-bold text-[#06B6D4] hover:bg-[#06B6D4]/10 rtl:ml-0 rtl:mr-auto">
                  💬 Chat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
